import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { EVENT_REGISTRANTS_QUERY, GENERATE_REGISTRATION_EXPORT_MUTATION } from "../../queries";
import { EventRegistrantNode, EventRegistrants, Variables, RegistrationStatus } from "../../types";
import {
  isRegistrationClosed,
  isNationalMeeting,
  isCancelRegistrationPerformable,
  getEventTypeName
} from "../../utils";
import { renderBillingStatus } from "@/pages/events/_utils";
import { toDateFormat } from "@/lib/dateHelpers";
import { type CellContext } from "@tanstack/react-table";
import Table, {
  TableHeading,
  TableControls,
  TableFooter,
  buildAvatarDetailsColumn,
  buildActionColumn,
  buildExpandableCountColumn
} from "@/components/Table";
import NotesTable from "@/features/global/components/NotesTable";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { NoteNotableValues } from "@/types";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import { useNavigate } from "@/router";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import CancelModal from "../../_components/CancelModal";
import CancelRegistrationInfoModal from "../../_components/CancelRegistrationInfoModal";
import { OrderStatus, RegistrantRow } from "../../types";
import { EventOutletContext } from "../_layout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import MetricGrid from "@/components/MetricGrid/MetricGrid";
import AddRegistrantModal from "./_components/AddRegistrantModal";
import DeleteModal from "./_components/DeleteModal";
import EditRegistrationModal from "./_components/EditRegistrationModal";
import TransferRegistrationModal from "./_components/TransferRegistrationModal";
import { EventWrapper } from "../../types";
import { useEventDetailQueryDocument, useIsUniversityEventsRoute } from "../../useEventDetailQuery";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import useCapabilities, { useHasRoles } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import OverrideRegistrationModal from "./_components/OverrideRegistrationModal";
import useCanCancelRegistration from "../../useCanCancelRegistration";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function RegistrationsPage() {
  const { eventSlug = "", universityId } = useParams();
  const basePath = useEventsBasePath();
  const isUniversityEventsRoute = useIsUniversityEventsRoute();
  const eventDetailQuery = useEventDetailQueryDocument();
  const { eventData, setErrorMessage } = useOutletContext<EventOutletContext>();
  const { isCompactLayout } = useIsCompactLayout();
  const { trackEvent } = useAmplitude();

  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrantRow | null>(null);
  const [modalType, setModalType] = useState<"cancel" | "cancel-info" | "delete" | "edit" | "transfer" | null>(null);
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const [variables, setVariables] = useState<Variables>({ eventSlug });
  const [addRegistrantDialogOpen, setAddRegistrantDialogOpen] = useState(false);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [isOverrideRegistrationDialogOpen, setIsOverrideRegistrationDialogOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const [addNoteRegistration, setAddNoteRegistration] = useState<{ id: string; pgaId: string } | null>(null);
  const { id: currentUserId } = useCapabilities();
  const currentUser = useCurrentBackofficeUser();
  const isSeminar = getEventTypeName(eventData)?.toLowerCase().includes("seminar");
  const isUniversitySeminar = getEventTypeName(eventData)?.toLowerCase().includes("university");
  const isEducationHq = useHasRoles([ROLE.EDUCATION_ADMIN, ROLE.GLOBAL_ADMIN]);
  const isActivitiesEventsRoute = basePath.startsWith("/activities/events");

  useEffect(() => {
    resetPagination();
    setVariables((prevVariables) => ({
      ...(debouncedSearch ? { ...prevVariables, search: debouncedSearch } : { eventSlug })
    }));
  }, [debouncedSearch, eventSlug, resetPagination]);

  const {
    data,
    loading: queryLoading,
    refetch
  } = useQuery<EventRegistrants>(EVENT_REGISTRANTS_QUERY, {
    variables: { status: RegistrationStatus.REGISTERED, ...variables, ...paginationVariables }
  });

  const { data: eventDataResponse } = useQuery<EventWrapper>(eventDetailQuery, {
    variables: { slug: eventSlug },
    fetchPolicy: "cache-first"
  });
  const [generateRegistrationExport, { loading: mutationLoading }] = useMutation(GENERATE_REGISTRATION_EXPORT_MUTATION);

  const handleCsvExport = async (eventSlug: string) => {
    try {
      const { data } = await generateRegistrationExport({
        variables: {
          input: {
            eventSlug
          }
        }
      });
      const downloadUrl = data.generateRegistrationExport.url;
      if (!downloadUrl) {
        throw new Error("Download URL not found.");
      }
      trackEvent(
        EVENT_ANALYTICS_EVENTS.registrationsExported,
        getEventAnalyticsData({
          source: "exports",
          eventId: eventData?.id,
          eventSlug,
          action: "export",
          field: "registrations"
        })
      );
      window.location.assign(downloadUrl);
    } catch (error) {
      alert("Unable to export registrations");
      console.error(error);
    }
  };

  const handleAddRegistrant = () => {
    setAddRegistrantDialogOpen(true);
  };

  const handleRegisterEligibleStudents = () => {
    if (!eventSlug) return;
    navigate(`${basePath}/${eventSlug}/manual-registrations` as any);
  };

  const registrationsData: RegistrantRow[] = (data?.eventRegistrants.edges || []).map(
    ({ node: { registeredAt, order, notes, ...registrant } }: EventRegistrantNode) => ({
      ...registrant,
      registeredAt: toDateFormat(registeredAt) || "",
      orderStatus: order?.status as OrderStatus,
      orderTotalPaid: order?.totalPaid,
      additionalGuests: registrant.customFields?.additional_guests || 0,
      notes: notes || [],
      university: registrant.university
    })
  );
  const hasAdditionalGuests = !!eventData?.customFields?.includes("additional_guests");
  const showUniversityColumn = isEducationHq && isSeminar;
  const canRegisterEligibleStudents =
    isUniversitySeminar &&
    !!eventData?.registrationCloseAt &&
    !isRegistrationClosed(eventData) &&
    (isUniversityEventsRoute || (isEducationHq && isActivitiesEventsRoute));

  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();

  const isSectionScoped = useIsSectionScopedForEvents();
  const canAccessActivityManagement =
    useActivityTypePermissions(eventData?.activityType ?? { title: getEventTypeName(eventData) }) &&
    (!isSectionScoped || !!eventData?.section?.permissions?.manageEvents);
  const canSeeCancelRegistration = useCanCancelRegistration(eventData, {
    canAccessActivityManagement
  });
  const canPerformCancelRegistration = isCancelRegistrationPerformable(eventData);

  const canManageNationalMeetingRegistrations =
    (useHasRoles([ROLE.MEMBERSHIP_SERVICES_STAFF, ROLE.PLAYER_ENGAGEMENT_STAFF, ROLE.GLOBAL_ADMIN]) ||
      canAccessActivityManagement) &&
    isNationalMeeting(eventData);

  const canOverrideRegistration = useHasRoles(eventData?.overrideRoles ?? []);
  const isFinalized = !!eventData?.finalizedAt;

  const isAlreadyRegisteredError = (pgaId: string) => {
    return registrationsData.some((registrant) => registrant.pgaId === pgaId);
  };

  const {
    expandRowId: highlightRegistrationId,
    highlightNoteId,
    openNoteId,
    tableRef
  } = useNoteDeepLink({ dataReady: !queryLoading });

  const renderNotesPanel = useCallback(
    (row: { original: RegistrantRow }) => {
      const notes = row.original.notes || [];
      if (notes.length === 0) {
        return <></>;
      }

      return (
        <NotesTable
          variant="details-panel"
          data={notes}
          title={`Notes (${notes.length})`}
          highlightNoteId={highlightNoteId}
          openNoteId={openNoteId}
          noteEditing={{
            enabled: canAccessActivityManagement,
            notableId: row.original.id,
            notableType: NoteNotableValues.EVENT_REGISTRATION,
            refetchQueries: [EVENT_REGISTRANTS_QUERY],
            pgaId: row.original.pgaId,
            objectType: "person"
          }}
        />
      );
    },
    [canAccessActivityManagement, highlightNoteId, openNoteId]
  );

  const columns = [
    ...buildAvatarDetailsColumn<RegistrantRow>({
      isCompactLayout,
      getTitle: (row) => row.registrantName,
      getSubtitle: (row) => row.programType,
      subtitleLabel: "Program",
      getPicture: (row) => row.profilePhoto,
      header: "Name",
      size: 500,
      minSize: 200,
      id: "name"
    }),
    { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
    ...(showUniversityColumn
      ? [
          {
            id: "university",
            header: "University",
            accessorFn: (row: RegistrantRow) => row.university?.name ?? "—",
            enableSorting: false
          }
        ]
      : []),
    ...(hasAdditionalGuests ? [{ accessorKey: "additionalGuests", header: "Guests", enableSorting: false }] : []),
    {
      accessorKey: "registeredAt",
      header: "Reg. Date",
      enableSorting: false,
      size: 160
    },
    {
      accessorKey: "sessionCount",
      header: "Sessions",
      enableSorting: false
    },
    ...(registrationsData.some((row) => row.orderStatus)
      ? [
          {
            cell: (cellProps: CellContext<RegistrantRow, unknown>) => {
              const { orderStatus } = cellProps.row.original;
              return renderBillingStatus(orderStatus);
            },
            id: "orderStatus",
            header: "Payment Status",
            meta: {
              headerClassName: "hide-header-in-column"
            }
          }
        ]
      : []),
    buildExpandableCountColumn<RegistrantRow>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes?.length ?? 0,
      autoExpandRowId: highlightRegistrationId
    }),
    buildActionColumn<RegistrantRow>({
      getActions: (row) => {
        const actions: Action[] = [
          {
            text: "View Registration",
            testId: "view-registration",
            section: "Manage",
            onClick: () => {
              navigate(`${basePath}/${eventSlug}/registrations/${row.id}` as any);
            }
          }
        ];

        if (canAccessActivityManagement) {
          actions.push({
            section: "Actions",
            text: "Add Note",
            testId: "add-registration-note",
            onClick: () => setAddNoteRegistration({ id: row.id, pgaId: row.pgaId })
          });
        }

        if (canManageNationalMeetingRegistrations) {
          actions.push({
            text: "Edit Registration",
            section: "Manage",
            onClick: () => {
              setSelectedRegistration(row);
              setModalType("edit");
            }
          });
        }

        if (canAccessActivityManagement && isSeminar && !isFinalized) {
          actions.push({
            text: "Transfer Registration",
            section: "Manage",
            onClick: () => {
              setSelectedRegistration(row);
              setModalType("transfer");
            }
          });
        }

        if (canSeeCancelRegistration && !isFinalized) {
          actions.push({
            text: "Cancel Registration",
            testId: "cancel-registration",
            destructive: true,
            onClick: () => {
              setSelectedRegistration(row);
              setModalType(canPerformCancelRegistration ? "cancel" : "cancel-info");
            }
          });
        }

        if (canManageNationalMeetingRegistrations) {
          actions.push({
            text: "Delete Registration",
            iconName: "delete",
            destructive: true,
            onClick: () => {
              setSelectedRegistration(row);
              setModalType("delete");
            }
          });
        }

        return actions;
      },
      onViewClick: (row) => {
        setSelectedPgaId(row.pgaId);
        setIsPersonSideSheetOpen(true);
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];

  return (
    <>
      {!!selectedRegistration && modalType === "cancel" && (
        <CancelModal
          event={eventData || {}}
          eventRegistrationId={selectedRegistration.id}
          open={modalType === "cancel"}
          setOpen={(value) => {
            if (!value) {
              setSelectedRegistration(null);
              setModalType(null);
            }
          }}
          onError={(message: string) => {
            setErrorMessage(message);
          }}
          order={{ status: selectedRegistration.orderStatus, totalPaid: selectedRegistration.orderTotalPaid }}
          refetch={refetch}
        />
      )}
      {modalType === "cancel-info" && (
        <CancelRegistrationInfoModal
          open
          onClosed={() => {
            setSelectedRegistration(null);
            setModalType(null);
          }}
        />
      )}
      {!!selectedRegistration && modalType === "delete" && (
        <DeleteModal
          selectedRegistration={selectedRegistration}
          eventRegistrationId={selectedRegistration.id}
          onClosed={() => {
            setSelectedRegistration(null);
            setModalType(null);
          }}
          refetch={refetch}
        />
      )}
      {!!selectedRegistration && modalType === "edit" && (
        <EditRegistrationModal
          event={eventDataResponse?.event}
          selectedRegistration={selectedRegistration}
          eventRegistrationId={selectedRegistration.id}
          onClosed={() => {
            setSelectedRegistration(null);
            setModalType(null);
          }}
          refetch={refetch}
        />
      )}
      {!!selectedRegistration && modalType === "transfer" && (
        <TransferRegistrationModal
          registrationId={selectedRegistration.id}
          registrantName={selectedRegistration.registrantName}
          registrantPgaId={selectedRegistration.pgaId}
          currentEventId={eventDataResponse?.event?.id || ""}
          onClosed={() => {
            setSelectedRegistration(null);
            setModalType(null);
          }}
          refetch={refetch}
        />
      )}
      <Menu />
      {addNoteRegistration && (
        <AddEditNoteSideSheet
          open={!!addNoteRegistration}
          onClose={() => setAddNoteRegistration(null)}
          notableId={addNoteRegistration.id}
          notableType={NoteNotableValues.EVENT_REGISTRATION}
          refetchQueries={[EVENT_REGISTRANTS_QUERY]}
          currentUserId={currentUserId || undefined}
          currentUser={currentUser}
          pgaId={addNoteRegistration.pgaId}
          objectType="person"
          onSaveSuccess={() => setAddNoteRegistration(null)}
        />
      )}
      {eventData?.cancelledAt && eventData.status === "Canceled" && (
        <MetricGrid
          metrics={[
            {
              iconName: "event_busy",
              label: "Event Cancellation Date",
              value: toDateFormat(eventData.cancelledAt) || "",
              loading: false
            }
          ]}
          layout="compact"
        />
      )}
      <TableHeading
        heading="Event Registrants"
        body="View all registrants for this event and manage registrations"
        actions={[
          ...(canManageNationalMeetingRegistrations
            ? [
                {
                  label: "Add Registrant",
                  icon: "add" as const,
                  variant: "outlined" as const,
                  onClick: () => handleAddRegistrant()
                }
              ]
            : []),
          ...(canRegisterEligibleStudents
            ? [
                {
                  label: "Register Eligible Students",
                  variant: "filled" as const,
                  onClick: handleRegisterEligibleStudents
                }
              ]
            : []),
          ...(isSeminar && (!isUniversitySeminar || isUniversityEventsRoute) && canOverrideRegistration
            ? [
                {
                  label: "Override Registration",
                  variant: "outlined" as const,
                  onClick: () => setIsOverrideRegistrationDialogOpen(true)
                }
              ]
            : []),
          ...(registrationsData.length > 0
            ? [
                {
                  label: "Export Current List",
                  variant: "text" as const,
                  icon: "download" as const,
                  disabled: mutationLoading,
                  onClick: () => handleCsvExport(eventSlug)
                }
              ]
            : [])
        ]}
      />
      <div ref={tableRef}>
        <Table
          data={registrationsData || []}
          loading={queryLoading}
          columns={columns}
          getRowCanExpand={(row) => (row.original.notes?.length ?? 0) > 0}
          renderExpandedRow={({ row }) => renderNotesPanel(row)}
          onClickRow={(row) => {
            navigate(`${basePath}/${eventSlug}/registrations/${row.original.id}` as any);
          }}
          renderControls={() => (
            <TableControls
              pagination={createHeaderPagination(data?.eventRegistrants, { pageCount: registrationsData.length })}
            >
              <SearchInput
                placeholder="Search Registrants"
                query={searchValue}
                onChange={setSearchValue}
                onClear={clearSearch}
              />
            </TableControls>
          )}
          hasActiveFilters={!!searchValue}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Registrations"
              caption="This event does not have any registrations"
              iconName="person"
            />
          )}
          renderFooter={() => (
            <TableFooter
              pagination={createFooterPagination(data?.eventRegistrants, { pageCount: registrationsData.length })}
            />
          )}
        />
      </div>
      {addRegistrantDialogOpen && (
        <AddRegistrantModal
          isAlreadyRegisteredError={isAlreadyRegisteredError}
          event={eventDataResponse?.event}
          onDismiss={() => setAddRegistrantDialogOpen(false)}
          eventId={eventDataResponse?.event?.id || ""}
          refetch={refetch}
        />
      )}
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
      {isOverrideRegistrationDialogOpen && (
        <OverrideRegistrationModal
          eventId={eventDataResponse?.event?.id || ""}
          universityLegacyId={universityId || ""}
          onDismiss={() => setIsOverrideRegistrationDialogOpen(false)}
          refetch={refetch}
        />
      )}
    </>
  );
}
